/**
 * Hook for synchronizing local SQLite database with Supabase
 * 
 * Handles:
 * - Downloading static data (products, categories, tables) from server
 * - Uploading pending operations from sync_queue
 * - Conflict resolution
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { useSQLiteContext } from '@/contexts/SQLiteContext';
import { useSQLiteLocal } from './useSQLiteLocal';
import { useRestaurantContext } from '@/contexts/RestaurantContext';
import { supabase } from '@/integrations/supabase/client';
import { getIsOnline } from '@/lib/connectivity';
import { 
  getPendingSyncOperations, 
  updateSyncQueueStatus, 
  clearCompletedSyncOperations,
  setSyncMeta,
  getSyncMeta
} from '@/lib/sqlite-local';
import { toast } from 'sonner';
import { logSyncDiscard } from '@/lib/sync-lock';

/**
 * Aguarda a sessão do Supabase estar disponível antes de prosseguir com o sync.
 * Após reconexão, o GoTrueClient pode ainda estar em _recoverAndRefresh,
 * fazendo com que auth.uid() retorne null e cause 403 em inserts protegidos por RLS.
 */
async function waitForSession(maxWaitMs = 10000): Promise<boolean> {
  const start = Date.now();
  while (Date.now() - start < maxWaitMs) {
    try {
      const { data } = await supabase.auth.getSession();
      if (data?.session?.access_token) {
        return true;
      }
    } catch (e) {
      // ignore and retry
    }
    await new Promise(r => setTimeout(r, 500));
  }
  return false;
}

interface SyncState {
  isOnline: boolean;
  isSyncing: boolean;
  lastSyncAt: Date | null;
  pendingCount: number;
  error: string | null;
}

export function useSQLiteSync() {
  const { isReady, syncStats, refreshSyncStats } = useSQLiteContext();
  const { 
    upsertProducts, 
    upsertCategories, 
    upsertTables, 
    upsertCommands,
    upsertPaymentMethods,
    upsertProductionLocations,
    upsertActiveConsumption,
    getAllActiveConsumptions,
    deleteActiveConsumption,
    getTables,
    getCommands,
  } = useSQLiteLocal();
  const { activeRestaurant } = useRestaurantContext();
  const restaurantId = activeRestaurant?.id;

  const [state, setState] = useState<SyncState>({
    isOnline: getIsOnline(),
    isSyncing: false,
    lastSyncAt: null,
    pendingCount: 0,
    error: null,
  });

  const syncInProgress = useRef(false);

  // Guard to prevent initial sync from racing with handleOnline
  const justCameOnlineRef = useRef(false);

  // Track online status
  useEffect(() => {
    const handleOnline = async () => {
      setState(prev => ({ ...prev, isOnline: true }));
      justCameOnlineRef.current = true;
      // Sequential: upload closures FIRST, then download fresh state
      if (isReady) {
        console.log('[SQLiteSync] Back online: waiting 1.5s for connection to stabilize...');
        // Delay to allow network and Supabase auth token to stabilize
        await new Promise(resolve => setTimeout(resolve, 1500));
        // waitForSession agora é executado dentro de syncToServer (cobre todos os caminhos)
        const result = await syncToServer();
        // Sempre atualizar cache de consumos ativos: syncActiveConsumptions só remove
        // entidades que não existem mais no servidor, então é seguro rodar mesmo
        // com falhas pendentes na fila.
        if (result.failed > 0) {
          console.warn(`[SQLiteSync] ${result.failed} operações falharam — atualizando cache de consumos ativos mesmo assim`);
        } else {
          console.log('[SQLiteSync] All uploads succeeded, downloading fresh state...');
        }
        await syncActiveConsumptions();
        justCameOnlineRef.current = false;
      }
    };
    const handleOffline = () => setState(prev => ({ ...prev, isOnline: false }));

    window.addEventListener('app:online', handleOnline);
    window.addEventListener('app:offline', handleOffline);

    return () => {
      window.removeEventListener('app:online', handleOnline);
      window.removeEventListener('app:offline', handleOffline);
    };
  }, [isReady]);

  // Update pending count from sync stats
  useEffect(() => {
    setState(prev => ({ ...prev, pendingCount: syncStats.pending + syncStats.syncing }));
  }, [syncStats]);

  // ============ Download Static Data ============

  const syncStaticData = useCallback(async (): Promise<boolean> => {
    if (!isReady || !restaurantId || !getIsOnline()) {
      return false;
    }

    try {
      console.log('[SQLiteSync] Syncing static data from server...');

      // Fetch all static data in parallel
      const [productsRes, categoriesRes, tablesRes, commandsRes, paymentMethodsRes, productionLocationsRes] = await Promise.all([
        supabase
          .from('products')
          .select('*')
          .eq('restaurant_id', restaurantId),
        supabase
          .from('categories')
          .select('*')
          .eq('restaurant_id', restaurantId),
        supabase
          .from('tables')
          .select('*')
          .eq('restaurant_id', restaurantId),
        supabase
          .from('commands')
          .select('*')
          .eq('restaurant_id', restaurantId),
        supabase
          .from('payment_methods')
          .select('*')
          .eq('restaurant_id', restaurantId),
        supabase
          .from('production_locations')
          .select('id, restaurant_id, name, printer_name, is_active')
          .eq('restaurant_id', restaurantId),
      ]);

      // Check for errors
      if (productsRes.error) throw productsRes.error;
      if (categoriesRes.error) throw categoriesRes.error;
      if (tablesRes.error) throw tablesRes.error;
      if (commandsRes.error) throw commandsRes.error;
      if (paymentMethodsRes.error) throw paymentMethodsRes.error;
      if (productionLocationsRes.error) {
        console.warn('[SQLiteSync] production_locations fetch failed (non-blocking):', productionLocationsRes.error);
      }

      // Insert data into SQLite
      if (productsRes.data?.length) {
        const validProducts = productsRes.data.filter(p => p.restaurant_id != null);
        if (validProducts.length) {
          try {
            upsertProducts(validProducts.map(p => ({
              id: p.id,
              restaurant_id: p.restaurant_id,
              name: p.name,
              price: Number(p.price),
              category_id: p.category_id,
              image_url: p.image_url,
              description: p.description,
              is_active: p.is_active ? 1 : 0,
              tracks_stock: p.tracks_stock ? 1 : 0,
              current_stock: p.current_stock,
              cost_per_unit: p.cost_per_unit,
              barcode: p.barcode,
              production_location_id: p.production_location_id,
              available_pdv: p.available_pdv !== false ? 1 : 0,
              available_table: p.available_table !== false ? 1 : 0,
              available_tablet: p.available_tablet !== false ? 1 : 0,
              is_featured: p.is_featured ? 1 : 0,
              sort_order: 0,
            })));
          } catch (err) {
            console.warn('[SQLiteSync] Failed to upsert products:', err);
          }
        }
      }

      if (categoriesRes.data?.length) {
        upsertCategories(categoriesRes.data.map(c => ({
          id: c.id,
          restaurant_id: c.restaurant_id,
          name: c.name,
          description: c.description,
          color: c.color,
          icon: c.icon,
          sort_order: c.sort_order || 0,
          is_active: c.is_active ? 1 : 0,
        })));
      }

      if (tablesRes.data?.length) {
        upsertTables(tablesRes.data.map(t => ({
          id: t.id,
          restaurant_id: t.restaurant_id,
          number: t.number,
          name: t.name,
          status: t.status || 'available',
          current_order_id: t.current_order_id,
          service_tax_enabled: t.service_tax_enabled !== false ? 1 : 0,
          capacity: t.capacity || 4,
          is_active: t.is_active !== false ? 1 : 0,
        })));
      }

      if (commandsRes.data?.length) {
        upsertCommands(commandsRes.data.map(c => ({
          id: c.id,
          restaurant_id: c.restaurant_id,
          number: c.number,
          status: c.status || 'available',
          current_order_id: c.current_order_id,
          customer_name: c.customer_name,
          service_tax_enabled: c.service_tax_enabled !== false ? 1 : 0,
          is_active: c.is_active !== false ? 1 : 0,
        })));
      }

      if (paymentMethodsRes.data?.length) {
        upsertPaymentMethods(paymentMethodsRes.data.map(m => ({
          id: m.id,
          restaurant_id: m.restaurant_id,
          name: m.name,
          type: m.type,
          fee_percentage: Number(m.fee_percentage) || 0,
          is_active: m.is_active !== false ? 1 : 0,
          accepts_change: (m as any).accepts_change ? 1 : 0,
        })));
      }

      if (productionLocationsRes.data?.length) {
        try {
          upsertProductionLocations(productionLocationsRes.data.map(l => ({
            id: l.id,
            restaurant_id: l.restaurant_id,
            name: l.name,
            printer_name: l.printer_name ?? null,
            is_active: l.is_active !== false ? 1 : 0,
          })));
        } catch (err) {
          console.warn('[SQLiteSync] Failed to upsert production_locations:', err);
        }
      }

      // Update sync metadata
      setSyncMeta('last_static_sync', new Date().toISOString());
      setSyncMeta('products_count', String(productsRes.data?.length || 0));
      setSyncMeta('categories_count', String(categoriesRes.data?.length || 0));

      console.log('[SQLiteSync] Static data synced successfully');
      return true;
    } catch (error) {
      console.error('[SQLiteSync] Error syncing static data:', error);
      return false;
    }
  }, [isReady, restaurantId, upsertProducts, upsertCategories, upsertTables, upsertCommands, upsertPaymentMethods, upsertProductionLocations]);

  // ============ Sync Active Consumptions ============

  const syncActiveConsumptions = useCallback(async (): Promise<boolean> => {
    if (!isReady || !restaurantId || !getIsOnline()) {
      return false;
    }

    try {
      console.log('[SQLiteSync] Syncing active consumptions...');

      // Fetch occupied AND grouped tables with their current orders
      const { data: occupiedTables, error: tablesError } = await supabase
        .from('tables')
        .select('id, number, current_order_id, grouped_to_order_id, status')
        .eq('restaurant_id', restaurantId)
        .in('status', ['occupied', 'grouped']);

      if (tablesError) {
        console.error('[SQLiteSync] Error fetching occupied tables, aborting to protect cache:', tablesError);
        return false;
      }

      // Fetch in-use AND grouped commands with their current orders
      const { data: inUseCommands, error: commandsError } = await supabase
        .from('commands')
        .select('id, number, current_order_id, grouped_to_order_id, status')
        .eq('restaurant_id', restaurantId)
        .in('status', ['in_use', 'grouped']);

      if (commandsError) {
        console.error('[SQLiteSync] Error fetching in-use commands, aborting to protect cache:', commandsError);
        return false;
      }

      // Build maps of table/command info by number for grouping
      const tableInfoByNumber = new Map<string, { id: string; number: string }>();
      const commandInfoByNumber = new Map<string, { id: string; number: string }>();

      for (const table of occupiedTables || []) {
        tableInfoByNumber.set(table.number, { id: table.id, number: table.number });
      }

      for (const command of inUseCommands || []) {
        commandInfoByNumber.set(command.number, { id: command.id, number: command.number });
      }

      // Collect all active entity IDs from server
      const serverEntityIds = new Set<string>();
      for (const table of occupiedTables || []) {
        serverEntityIds.add(table.id);
      }
      for (const command of inUseCommands || []) {
        serverEntityIds.add(command.id);
      }

      // Delete local caches that are no longer active on server (prevents ghost items)
      try {
        const allLocal = getAllActiveConsumptions();
        for (const local of allLocal) {
          if (!serverEntityIds.has(local.id)) {
            console.log(`[SQLiteSync] Removing stale consumption cache: ${local.id} (${local.type} ${local.number})`);
            deleteActiveConsumption(local.id);
            // Instrumentation: log every cache delete so we can see if this
            // is the cause of "table empty on map / items inside" reports.
            void (async () => {
              try {
                if (!restaurantId) return;
                await supabase.from('error_logs').insert([{
                  restaurant_id: restaurantId,
                  source: 'cache_cleanup',
                  error_code: 'not_in_server_active_set',
                  error_message: `Removed stale cache ${local.type} ${local.number}`,
                  context: {
                    entity_id: local.id,
                    entity_type: local.type,
                    entity_number: local.number,
                    timestamp: new Date().toISOString(),
                  } as any,
                }]);
              } catch {}
            })();
          }
        }
      } catch (err) {
        console.warn('[SQLiteSync] Error cleaning stale consumptions:', err);
      }

      // Collect table numbers and command numbers to find ALL active orders
      // For grouped entities, use grouped_to_order_id directly
      const tableNumbers = (occupiedTables || [])
        .filter(t => t.status === 'occupied')
        .map(t => t.number)
        .filter(Boolean);
      const commandNumbers = (inUseCommands || [])
        .filter(c => c.status === 'in_use')
        .map(c => c.number)
        .filter(Boolean);

      // Collect order IDs from grouped entities (they point to the consolidated order)
      const groupedOrderIds = new Set<string>();
      for (const t of occupiedTables || []) {
        if (t.status === 'grouped' && t.grouped_to_order_id) {
          groupedOrderIds.add(t.grouped_to_order_id);
        }
      }
      for (const c of inUseCommands || []) {
        if (c.status === 'grouped' && c.grouped_to_order_id) {
          groupedOrderIds.add(c.grouped_to_order_id);
        }
      }

      if (tableNumbers.length === 0 && commandNumbers.length === 0 && groupedOrderIds.size === 0) {
        console.log('[SQLiteSync] No active consumptions to sync');
        return true;
      }

      // Fetch ALL active orders by table_number and tab_number (captures multiple orders per table)
      let tableOrders: any[] = [];
      let commandOrders: any[] = [];

      if (tableNumbers.length > 0) {
        const { data, error } = await supabase
          .from('orders')
          .select('id, order_number, table_number, tab_number, order_type')
          .eq('restaurant_id', restaurantId)
          .eq('order_type', 'table')
          .in('table_number', tableNumbers)
          .in('status', ['pending', 'preparing']);
        if (error) {
          console.error('[SQLiteSync] Error fetching table orders:', error);
          return false;
        }
        tableOrders = data || [];
      }

      if (commandNumbers.length > 0) {
        const { data, error } = await supabase
          .from('orders')
          .select('id, order_number, table_number, tab_number, order_type')
          .eq('restaurant_id', restaurantId)
          .eq('order_type', 'tab')
          .in('tab_number', commandNumbers)
          .in('status', ['pending', 'preparing']);
        if (error) {
          console.error('[SQLiteSync] Error fetching command orders:', error);
          return false;
        }
        commandOrders = data || [];
      }

      // Also fetch orders referenced by grouped entities
      let groupedOrders: any[] = [];
      if (groupedOrderIds.size > 0) {
        const { data, error } = await supabase
          .from('orders')
          .select('id, order_number, table_number, tab_number, order_type')
          .in('id', Array.from(groupedOrderIds))
          .in('status', ['pending', 'preparing']);
        if (error) {
          console.error('[SQLiteSync] Error fetching grouped orders:', error);
        } else {
          groupedOrders = data || [];
        }
      }

      const allOrders = [...tableOrders, ...commandOrders, ...groupedOrders];
      // Deduplicate by order id
      const seenOrderIds = new Set<string>();
      const uniqueOrders = allOrders.filter(o => {
        if (seenOrderIds.has(o.id)) return false;
        seenOrderIds.add(o.id);
        return true;
      });
      const allOrderIds = uniqueOrders.map((o: any) => o.id);

      if (allOrderIds.length === 0) {
        console.log('[SQLiteSync] No active orders found for occupied tables/commands');
        return true;
      }

      // Fetch all order items
      const { data: allItems, error: itemsError } = await supabase
        .from('order_items')
        .select('*')
        .in('order_id', allOrderIds)
        .order('created_at', { ascending: true });

      if (itemsError) {
        console.error('[SQLiteSync] Error fetching order items:', itemsError);
        return false;
      }

      // Group items by order_id
      const itemsByOrder = new Map<string, any[]>();
      for (const item of allItems || []) {
        const existing = itemsByOrder.get(item.order_id) || [];
        existing.push({
          id: item.id,
          order_id: item.order_id,
          product_id: item.product_id,
          combo_id: item.combo_id,
          product_name: item.product_name,
          quantity: item.quantity,
          unit_price: Number(item.unit_price),
          total_price: Number(item.total_price),
          notes: item.notes,
          status: 'sent',
          sent_to_kitchen: 1,
          created_at: item.created_at,
          synced: 1,
          server_id: item.id,
        });
        itemsByOrder.set(item.order_id, existing);
      }

      // Group orders by table number / command number, merging all items
      let cachedCount = 0;

      // Tables: group all orders by table_number, merge items
      const ordersByTableNumber = new Map<string, any[]>();
      for (const order of allOrders) {
        if (order.order_type === 'table' && order.table_number) {
          const existing = ordersByTableNumber.get(order.table_number) || [];
          existing.push(order);
          ordersByTableNumber.set(order.table_number, existing);
        }
      }

      for (const [tableNum, orders] of ordersByTableNumber) {
        const tableInfo = tableInfoByNumber.get(tableNum);
        if (!tableInfo) continue;
        
        // Merge items from ALL orders on this table
        const mergedItems: any[] = [];
        let latestOrderId = orders[0].id;
        let latestOrderNumber = orders[0].order_number;
        
        for (const order of orders) {
          const orderItems = itemsByOrder.get(order.id) || [];
          mergedItems.push(...orderItems);
        }

        try {
          upsertActiveConsumption(tableInfo.id, 'table', latestOrderId, latestOrderNumber, tableInfo.number, mergedItems);
          cachedCount++;
        } catch (err) {
          console.warn('[SQLiteSync] Failed to cache table consumption:', err);
        }
      }

      // Commands: group all orders by tab_number, merge items
      const ordersByCommandNumber = new Map<string, any[]>();
      for (const order of allOrders) {
        if (order.order_type === 'tab' && order.tab_number) {
          const existing = ordersByCommandNumber.get(order.tab_number) || [];
          existing.push(order);
          ordersByCommandNumber.set(order.tab_number, existing);
        }
      }

      for (const [cmdNum, orders] of ordersByCommandNumber) {
        const commandInfo = commandInfoByNumber.get(cmdNum);
        if (!commandInfo) continue;
        
        const mergedItems: any[] = [];
        let latestOrderId = orders[0].id;
        let latestOrderNumber = orders[0].order_number;
        
        for (const order of orders) {
          const orderItems = itemsByOrder.get(order.id) || [];
          mergedItems.push(...orderItems);
        }

        try {
          upsertActiveConsumption(commandInfo.id, 'command', latestOrderId, latestOrderNumber, commandInfo.number, mergedItems);
          cachedCount++;
        } catch (err) {
          console.warn('[SQLiteSync] Failed to cache command consumption:', err);
        }
      }

      // Cache grouped entities: they should show the same items as the consolidated order
      for (const table of occupiedTables || []) {
        if (table.status === 'grouped' && table.grouped_to_order_id) {
          const orderItems = itemsByOrder.get(table.grouped_to_order_id) || [];
          if (orderItems.length > 0) {
            try {
              // Find the order info for the grouped order
              const groupedOrder = uniqueOrders.find(o => o.id === table.grouped_to_order_id);
              upsertActiveConsumption(
                table.id, 'table', 
                table.grouped_to_order_id, 
                groupedOrder?.order_number || null, 
                table.number, 
                orderItems
              );
              cachedCount++;
            } catch (err) {
              console.warn('[SQLiteSync] Failed to cache grouped table consumption:', err);
            }
          }
        }
      }

      for (const cmd of inUseCommands || []) {
        if (cmd.status === 'grouped' && cmd.grouped_to_order_id) {
          const orderItems = itemsByOrder.get(cmd.grouped_to_order_id) || [];
          if (orderItems.length > 0) {
            try {
              const groupedOrder = uniqueOrders.find(o => o.id === cmd.grouped_to_order_id);
              upsertActiveConsumption(
                cmd.id, 'command', 
                cmd.grouped_to_order_id, 
                groupedOrder?.order_number || null, 
                cmd.number, 
                orderItems
              );
              cachedCount++;
            } catch (err) {
              console.warn('[SQLiteSync] Failed to cache grouped command consumption:', err);
            }
          }
        }
      }

      setSyncMeta('last_consumption_sync', new Date().toISOString());
      setSyncMeta('cached_consumptions', String(cachedCount));
      
      console.log(`[SQLiteSync] Cached ${cachedCount} active consumptions (multi-order merge)`);

      return true;
    } catch (error) {
      console.error('[SQLiteSync] Error syncing active consumptions:', error);
      return false;
    }
  }, [isReady, restaurantId, upsertActiveConsumption, getAllActiveConsumptions, deleteActiveConsumption]);

  // ============ Upload Pending Operations ============

  type SyncOutcome = 'success' | 'conflict' | 'permanent' | 'transient';

  function classifyError(err: any): SyncOutcome {
    if (!err) return 'success';
    const code = err.code || err.error?.code;
    const status = err.status || err.statusCode;
    const msg = (err.message || '').toLowerCase();

    // Idempotente: registro já existe no servidor
    if (
      status === 409 ||
      code === '23505' ||
      msg.includes('duplicate key') ||
      msg.includes('already exists')
    ) return 'conflict';

    // Permanente: permissão / RLS — não adianta retentar
    if (
      status === 401 || status === 403 ||
      code === '42501' || code === 'PGRST301' || code === 'PGRST302' ||
      msg.includes('row-level security') ||
      msg.includes('permission denied') ||
      msg.includes('not authorized')
    ) return 'permanent';

    return 'transient';
  }

  const syncToServer = useCallback(async (): Promise<{ success: number; failed: number }> => {
    if (!isReady || !getIsOnline() || syncInProgress.current) {
      return { success: 0, failed: 0 };
    }
    let success = 0;
    let failed = 0;

    try {
      syncInProgress.current = true;
      setState(prev => ({ ...prev, isSyncing: true, error: null }));

      // Aguardar sessão Supabase válida antes de qualquer insert/update/delete.
      // Cobre todos os caminhos (handleOnline, initial sync, periodic interval).
      console.log('[SQLiteSync] Waiting for session before sync...');
      const sessionReady = await waitForSession(10000);
      if (!sessionReady) {
        console.warn('[SQLiteSync] Session not ready after 10s, aborting');
        return { success: 0, failed: 0 };
      }
      console.log('[SQLiteSync] Session ready, starting sync');

      const pending = getPendingSyncOperations(50);
      
      if (pending.length === 0) {
        console.log('[SQLiteSync] No pending operations to sync');
        return { success: 0, failed: 0 };
      }

      console.log(`[SQLiteSync] Processing ${pending.length} pending operations...`);

      for (const op of pending) {
        try {
          // Mark as syncing
          updateSyncQueueStatus(op.id, 'syncing');

          const payload = op.payload ? JSON.parse(op.payload) : null;
          let error: Error | null = null;

          switch (op.operation) {
            case 'INSERT':
              error = await handleInsert(op.table_name, op.record_id, payload);
              break;
            case 'UPDATE':
              error = await handleUpdate(op.table_name, op.record_id, payload);
              break;
            case 'DELETE':
              error = await handleDelete(op.table_name, op.record_id);
              break;
          }

          const outcome = classifyError(error);
          if (outcome === 'success' || outcome === 'conflict') {
            if (outcome === 'conflict') {
              console.log(`[SQLiteSync] Operação ${op.operation} ${op.table_name}/${op.record_id} já existe no servidor (409/23505) — tratando como sincronizada`);
            }
            updateSyncQueueStatus(op.id, 'done');
            success++;
          } else if (outcome === 'permanent') {
            const status = (error as any)?.status;
            const isOrderPayments403Insert =
              op.operation === 'INSERT' && op.table_name === 'order_payments' && status === 403;
            if (isOrderPayments403Insert) {
              // Tentativa atual = op.attempts + 1 (será incrementado por updateSyncQueueStatus)
              const currentAttempt = (op.attempts || 0) + 1;
              if (currentAttempt >= 3) {
                console.error(`[SQLiteSync] order_payments INSERT/${op.record_id} 403 persistente após ${currentAttempt} tentativas — descartando da fila`);
                updateSyncQueueStatus(op.id, 'done', `permission_denied_discarded_after_${currentAttempt}_attempts: ${error!.message}`);
                logSyncDiscard({
                  restaurantId,
                  reason: 'order_payments_403_discarded',
                  operation: op.operation,
                  table: op.table_name,
                  recordId: op.record_id,
                  orderId: (payload as any)?.order_id,
                  attempts: currentAttempt,
                  errorMessage: error!.message,
                  payload,
                });
              } else {
                console.warn(`[SQLiteSync] order_payments INSERT/${op.record_id} 403 (tentativa ${currentAttempt}/3) — manterá retry`);
                updateSyncQueueStatus(op.id, 'failed', error!.message);
              }
            } else {
              console.error(`[SQLiteSync] Falha permanente (${(error as any)?.code || status}) em ${op.operation} ${op.table_name}/${op.record_id} — removendo da fila:`, error!.message);
              // Marca como 'done' para que clearCompletedSyncOperations remova da fila
              // e não bloqueie o download de estado fresco em handleOnline.
              updateSyncQueueStatus(op.id, 'done', `permission_denied: ${error!.message}`);
              logSyncDiscard({
                restaurantId,
                reason: 'permanent_failure_discarded',
                operation: op.operation,
                table: op.table_name,
                recordId: op.record_id,
                orderId: (payload as any)?.order_id,
                attempts: (op.attempts || 0) + 1,
                errorMessage: error!.message,
                payload,
              });
            }
            failed++;
          } else {
            updateSyncQueueStatus(op.id, 'failed', error!.message);
            failed++;
          }
        } catch (err) {
          const outcome = classifyError(err);
          if (outcome === 'conflict') {
            console.log(`[SQLiteSync] Operação ${op.operation} ${op.table_name}/${op.record_id} já existe no servidor (catch) — tratando como sincronizada`);
            updateSyncQueueStatus(op.id, 'done');
            success++;
          } else if (outcome === 'permanent') {
            const status = (err as any)?.status;
            const msg = err instanceof Error ? err.message : 'Unknown error';
            const isOrderPayments403Insert =
              op.operation === 'INSERT' && op.table_name === 'order_payments' && status === 403;
            if (isOrderPayments403Insert) {
              const currentAttempt = (op.attempts || 0) + 1;
              if (currentAttempt >= 3) {
                console.error(`[SQLiteSync] order_payments INSERT/${op.record_id} 403 persistente (catch) após ${currentAttempt} tentativas — descartando da fila`);
                updateSyncQueueStatus(op.id, 'done', `permission_denied_discarded_after_${currentAttempt}_attempts: ${msg}`);
                logSyncDiscard({
                  restaurantId,
                  reason: 'order_payments_403_discarded',
                  operation: op.operation,
                  table: op.table_name,
                  recordId: op.record_id,
                  attempts: currentAttempt,
                  errorMessage: msg,
                });
              } else {
                console.warn(`[SQLiteSync] order_payments INSERT/${op.record_id} 403 (catch, tentativa ${currentAttempt}/3) — manterá retry`);
                updateSyncQueueStatus(op.id, 'failed', msg);
              }
            } else {
              console.error(`[SQLiteSync] Falha permanente (catch) em ${op.operation} ${op.table_name}/${op.record_id} — removendo da fila:`, err);
              updateSyncQueueStatus(op.id, 'done', `permission_denied: ${msg}`);
              logSyncDiscard({
                restaurantId,
                reason: 'permanent_failure_discarded',
                operation: op.operation,
                table: op.table_name,
                recordId: op.record_id,
                attempts: (op.attempts || 0) + 1,
                errorMessage: msg,
              });
            }
            failed++;
          } else {
            console.error('[SQLiteSync] Error processing operation:', op, err);
            updateSyncQueueStatus(op.id, 'failed', err instanceof Error ? err.message : 'Unknown error');
            failed++;
          }
        }
      }

      // Clean up completed operations
      clearCompletedSyncOperations();
      refreshSyncStats();

      if (success > 0) {
        console.log(`[SQLiteSync] Synced ${success} operations successfully`);
      }
      if (failed > 0) {
        console.warn(`[SQLiteSync] ${failed} operations failed`);
      }

      setState(prev => ({ 
        ...prev, 
        lastSyncAt: new Date(),
        error: failed > 0 ? `${failed} operações falharam` : null
      }));

      return { success, failed };
    } catch (error) {
      console.error('[SQLiteSync] Sync error:', error);
      setState(prev => ({ ...prev, error: 'Erro na sincronização' }));
      return { success, failed };
    } finally {
      syncInProgress.current = false;
      setState(prev => ({ ...prev, isSyncing: false }));
    }
  }, [isReady, refreshSyncStats, restaurantId]);

  // ============ Payload Sanitization ============

  const ORDERS_ALLOWED_COLUMNS = new Set([
    'status', 'subtotal', 'discount', 'discount_type', 'additional_fees',
    'total', 'people_count', 'notes', 'updated_at', 'customer_name',
    'waiter_name', 'order_type', 'table_number', 'tab_number',
    'table_id', 'command_id', 'period_id', 'cash_register_id',
    'couvert_quantity', 'couvert_amount', 'restaurant_id',
    'payment_method_id', 'payment_fee', 'payment_label', 'closed_at',
    'customer_phone', 'delivery_address', 'delivery_fee',
    'is_public_order', 'active_rodizio_combo_id',
  ]);

  const ORDERS_KEY_MAP: Record<string, string> = {
    'service_tax': 'additional_fees',
  };

  function sanitizeOrderPayload(payload: Record<string, unknown>): Record<string, unknown> {
    const sanitized: Record<string, unknown> = {};

    for (const [key, value] of Object.entries(payload)) {
      // Map legacy keys
      const mappedKey = ORDERS_KEY_MAP[key] || key;

      // Skip unknown columns
      if (!ORDERS_ALLOWED_COLUMNS.has(mappedKey)) {
        console.warn(`[SQLiteSync] Dropping unknown order column: ${key}`);
        continue;
      }

      // Skip NaN values
      if (typeof value === 'number' && isNaN(value)) {
        console.warn(`[SQLiteSync] Dropping NaN value for order column: ${mappedKey}`);
        continue;
      }

      // Skip empty string UUIDs
      if (value === '' && (mappedKey.endsWith('_id') || mappedKey === 'id')) {
        continue;
      }

      sanitized[mappedKey] = value;
    }

    // Always set updated_at
    if (!sanitized.updated_at) {
      sanitized.updated_at = new Date().toISOString();
    }

    return sanitized;
  }

  // ============ Operation Handlers ============

  async function handleInsert(tableName: string, recordId: string, payload: Record<string, unknown>): Promise<Error | null> {
    try {
      switch (tableName) {
        case 'orders': {
          // Use insert with explicit object - cast to expected types
          const orderType = payload.order_type as 'counter' | 'tab' | 'table' | 'delivery' | undefined;
          const orderStatus = (payload.status as 'pending' | 'preparing' | 'ready' | 'delivered' | 'cancelled') || 'pending';
          const { error } = await supabase.from('orders').insert([{
            id: recordId,
            restaurant_id: payload.restaurant_id as string,
            order_number: payload.order_number as number,
            order_type: orderType,
            table_number: payload.table_number as string || undefined,
            tab_number: payload.tab_number as string || undefined,
            customer_name: payload.customer_name as string || undefined,
            subtotal: payload.subtotal as number,
            discount: payload.discount as number || 0,
            total: payload.total as number,
            status: orderStatus,
          }]);
          if (error) throw error;
          break;
        }
        case 'order_items': {
          const itemStatus = (payload.status as 'pending' | 'preparing' | 'ready' | 'delivered' | 'cancelled') || 'pending';
          const { error } = await supabase.from('order_items').insert([{
            id: recordId,
            order_id: payload.order_id as string,
            product_id: payload.product_id as string || undefined,
            product_name: payload.product_name as string,
            quantity: payload.quantity as number,
            unit_price: payload.unit_price as number,
            total_price: payload.total_price as number,
            notes: payload.notes as string || undefined,
            status: itemStatus,
          }]);
          if (error) throw error;
          break;
        }
        case 'order_payments': {
          const { error } = await supabase.from('order_payments').insert([{
            id: recordId,
            order_id: payload.order_id as string,
            payment_method_id: payload.payment_method_id as string,
            amount: payload.amount as number,
            fee_amount: payload.fee_amount as number || 0,
          }]);
          if (error) throw error;
          break;
        }
        default:
          console.warn(`[SQLiteSync] Unknown table for INSERT: ${tableName}`);
      }
      return null;
    } catch (error) {
      return error instanceof Error ? error : new Error('Insert failed');
    }
  }

  async function handleUpdate(tableName: string, recordId: string, payload: Record<string, unknown>): Promise<Error | null> {
    try {
      switch (tableName) {
        case 'orders': {
          // Sanitize payload: whitelist valid columns and map legacy keys
          const sanitized = sanitizeOrderPayload(payload);
          const { error } = await supabase.from('orders').update(sanitized).eq('id', recordId);
          if (error) {
            console.error(`[SQLiteSync] Order update failed (${error.code}):`, error.message, error.details, error.hint, 'Payload:', sanitized);
            // Fallback: try minimal update with just status + updated_at
            if (sanitized.status) {
              console.log('[SQLiteSync] Attempting minimal fallback update (status only)...');
              const { error: fallbackError } = await supabase.from('orders')
                .update({ status: sanitized.status as any, updated_at: new Date().toISOString() })
                .eq('id', recordId);
              if (fallbackError) throw fallbackError;
              console.log('[SQLiteSync] Fallback update succeeded');
              break;
            }
            throw error;
          }
          break;
        }
        case 'order_items': {
          const { error } = await supabase.from('order_items').update(payload).eq('id', recordId);
          if (error) throw error;
          break;
        }
        case 'tables': {
          const { error } = await supabase.from('tables').update(payload).eq('id', recordId);
          if (error) throw error;
          break;
        }
        case 'commands': {
          const { error } = await supabase.from('commands').update(payload).eq('id', recordId);
          if (error) throw error;
          break;
        }
        default:
          console.warn(`[SQLiteSync] Unknown table for UPDATE: ${tableName}`);
      }
      return null;
    } catch (error) {
      return error instanceof Error ? error : new Error('Update failed');
    }
  }

  async function handleDelete(tableName: string, recordId: string): Promise<Error | null> {
    try {
      switch (tableName) {
        case 'order_items': {
          const { error } = await supabase.from('order_items').delete().eq('id', recordId);
          if (error) throw error;
          break;
        }
        default:
          console.warn(`[SQLiteSync] Unknown table for DELETE: ${tableName}`);
      }
      return null;
    } catch (error) {
      return error instanceof Error ? error : new Error('Delete failed');
    }
  }

  // ============ Full Sync ============

  const fullSync = useCallback(async (): Promise<void> => {
    if (!state.isOnline) {
      toast.error('Sem conexão com a internet');
      return;
    }

    setState(prev => ({ ...prev, isSyncing: true }));

    try {
      // First download static data
      const staticSuccess = await syncStaticData();
      if (!staticSuccess) {
        toast.error('Erro ao baixar dados do servidor');
      }

      // Upload pending changes BEFORE downloading consumptions
      const { success, failed } = await syncToServer();

      // Only download active consumptions if all uploads succeeded
      if (failed === 0) {
        await syncActiveConsumptions();
      } else {
        console.warn(`[SQLiteSync] fullSync: skipping consumption download — ${failed} uploads failed`);
      }
      
      if (failed === 0 && success > 0) {
        toast.success(`${success} operações sincronizadas`);
      } else if (failed > 0) {
        toast.warning(`${success} sincronizadas, ${failed} falharam`);
      } else if (staticSuccess) {
        toast.success('Dados sincronizados');
      }
    } catch (error) {
      console.error('[SQLiteSync] Full sync error:', error);
      toast.error('Erro na sincronização');
    } finally {
      setState(prev => ({ ...prev, isSyncing: false }));
    }
  }, [state.isOnline, syncStaticData, syncActiveConsumptions, syncToServer]);

  // ============ Initial Sync ============

  useEffect(() => {
    if (!isReady || !restaurantId || !state.isOnline) return;
    // Skip if handleOnline is already handling the sync sequence
    if (justCameOnlineRef.current) {
      console.log('[SQLiteSync] Skipping initial sync — handleOnline is already processing');
      return;
    }

    // Delay initial sync by 2 seconds to allow Realtime channels to stabilize first
    const timer = setTimeout(async () => {
      // Re-check: handleOnline might have started during the timeout
      if (justCameOnlineRef.current) return;

      const lastSync = getSyncMeta('last_static_sync');
      const shouldSync = !lastSync || (Date.now() - new Date(lastSync).getTime() > 5 * 60 * 1000);

      if (shouldSync) {
        console.log('[SQLiteSync] Starting delayed initial sync (allowing Realtime to stabilize)...');
        await syncStaticData();
      }
      
      const lastConsumptionSync = getSyncMeta('last_consumption_sync');
      const shouldSyncConsumptions = !lastConsumptionSync || (Date.now() - new Date(lastConsumptionSync).getTime() > 2 * 60 * 1000);
      
      if (shouldSyncConsumptions) {
        console.log('[SQLiteSync] Syncing active consumptions for offline access...');
        await syncActiveConsumptions();
      }
    }, 2000);

    return () => clearTimeout(timer);
  }, [isReady, restaurantId, state.isOnline, syncStaticData, syncActiveConsumptions]);

  // ============ Periodic Sync ============

  useEffect(() => {
    if (!isReady || !state.isOnline) return;

    // Sync pending + failed operations every 30 seconds
    const interval = setInterval(async () => {
      if ((syncStats.pending + syncStats.failed) > 0) {
        const result = await syncToServer();
        // If retries cleared all failures, now safe to refresh consumption cache
        if (result.failed === 0 && result.success > 0) {
          console.log('[SQLiteSync] Periodic retry succeeded, refreshing consumption cache...');
          await syncActiveConsumptions();
        }
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [isReady, state.isOnline, syncStats.pending, syncStats.failed, syncToServer, syncActiveConsumptions]);

  return {
    ...state,
    syncStaticData,
    syncActiveConsumptions,
    syncToServer,
    fullSync,
  };
}
